/**
 * SportIcon — in-house line glyphs for the sports (24 x 24, 2 stroke, currentColor), instead of emoji that look different on every phone.
 * Keys are the practice keys (badminton, tennis, table_tennis, pickleball, squash, basketball, cricket, gym, lifting) plus football and swimming.
 * The markup is static, written here, never user input.
 */
export const SPORT_ICONS = {
  // a shuttlecock: cork and the feather skirt
  badminton: '<circle cx="12" cy="18.5" r="2.5"/><path d="M9.7 16.8 6 5.5M14.3 16.8 18 5.5M12 16V4.5M6 5.5Q12 2.8 18 5.5"/>',
  // a ball with its two seams
  tennis: '<circle cx="12" cy="12" r="9"/><path d="M5.2 5.8c3.2 2.4 3.2 9.9 0 12.4M18.8 5.8c-3.2 2.4-3.2 9.9 0 12.4"/>',
  // a paddle and a ball
  table_tennis: '<circle cx="11.5" cy="9.5" r="6.5"/><path d="M9.6 15.6v5h3.8v-5"/><circle cx="20" cy="4.5" r="1.3"/>',
  // a ball with holes
  pickleball: '<circle cx="12" cy="12" r="9"/><path d="M9 8.5h.01M14.5 8h.01M16 13h.01M10.5 14h.01M7.5 12.5h.01M13 17h.01"/>',
  // a long narrow racquet
  // a squash racquet (long narrow head with strings, a throat and a handle), tilted
  squash: '<g transform="rotate(40 12 12)"><ellipse cx="12" cy="8" rx="4.2" ry="6"/><path d="M12 2v12M7.8 8h8.4M9.5 13.6 12 17l2.5-3.4M12 17v5"/></g>',
  basketball: '<circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3v18M5.6 5.6c3.2 3.2 3.2 9.6 0 12.8M18.4 5.6c-3.2 3.2-3.2 9.6 0 12.8"/>',
  // a bat and a ball
  cricket: '<g transform="rotate(35 12 12)"><path d="M9 9.5h6V19a1.8 1.8 0 0 1-1.8 1.8h-2.4A1.8 1.8 0 0 1 9 19Z"/><path d="M12 9.5V3M10.7 3h2.6M12 12.5v5.5"/></g><circle cx="5.6" cy="18.6" r="2.2"/>',
  // a kettlebell
  gym: '<circle cx="12" cy="14.5" r="6"/><path d="M9 9.8C8 3.6 16 3.6 15 9.8"/>',
  // a barbell
  lifting: '<path d="M2.5 12h19M7 7v10M17 7v10M4.2 9.5v5M19.8 9.5v5"/>',
  football: '<circle cx="12" cy="12" r="9"/><path d="m12 8.8 3 2.2-1.1 3.6h-3.8L9 11ZM12 8.8V3.2M15 11l5.2-1.7M13.9 14.6l3.2 4.4M10.1 14.6l-3.2 4.4M9 11 3.8 9.3"/>',
  // a swimmer's head and arm over two waves
  swimming: '<circle cx="16.5" cy="7" r="1.9"/><path d="m5 12.5 5-3.6 3.3 1.6 2.2-1.7M2.5 16.5c1.6 0 1.6-1.4 3.2-1.4s1.6 1.4 3.2 1.4 1.6-1.4 3.2-1.4 1.6 1.4 3.2 1.4 1.6-1.4 3.2-1.4M2.5 20.5c1.6 0 1.6-1.4 3.2-1.4s1.6 1.4 3.2 1.4 1.6-1.4 3.2-1.4 1.6 1.4 3.2 1.4 1.6-1.4 3.2-1.4"/>',
};

/**
 * `size` is the size on a phone (never under 18 px: a 16 px glyph is hard to read); from lg up it is 1.3 times that, so icons are not tiny on a laptop.
 * Both sizes are classes (CSS variables), not a zoom, so a caller's own `className` can still override either.
 */
export default function SportIcon({ sport, size = 28, className = "" }) {
  const inner = SPORT_ICONS[sport] || SPORT_ICONS.tennis;
  const base = Math.max(size, 18);
  return (
    <svg width={base} height={base} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ "--si": `${base}px`, "--sl": `${Math.round(base * 1.3)}px` }}
      className={`w-[var(--si)] h-[var(--si)] lg:w-[var(--sl)] lg:h-[var(--sl)] ${className}`} aria-hidden="true" focusable="false" dangerouslySetInnerHTML={{ __html: inner }} />
  );
}